import { gql } from '@apollo/client'
import { useContext, useEffect, useRef } from 'react'

import { useChooseFrequencyLaterEvents } from '../../../hooks/use-choose-frequency-later-events'
import { ChooseFrequencyLaterSteps } from '../../choose-frequency-later-steps'
import { ChooseFrequencyLaterStepsContent } from '../../choose-frequency-later-steps/components'
import {
  ChooseFrequencyLaterContextProvider,
  useChooseOfferFrequencyLaterContext,
} from '../../choose-frequency-later-steps/context-provider'
import { ChooseFrequencyLaterModalSteps } from '../../choose-frequency-later-steps/types'
import { BoxLockContext } from '../../context'
import { ModalLock } from '../../dynamic-multiple-offers-switch-frequency-lock/components'
import { PaywallLock } from '../../paywall-lock'
import {
  Upgrade2StepMessageLimitsLock_OfferLockDataFragment,
  Upgrade2StepMessageLimitsLock_ViewerFragment,
} from './__generated__/message-limits.graphql'

export interface Upgrade2StepMessageLimitsLockProps {
  offerLockData: Upgrade2StepMessageLimitsLock_OfferLockDataFragment
  viewer: Upgrade2StepMessageLimitsLock_ViewerFragment
}

export function Upgrade2StepMessageLimitsLock({
  offerLockData,
  viewer,
}: Upgrade2StepMessageLimitsLockProps) {
  const { componentType } = useContext(BoxLockContext)

  if (componentType === 'paywall') {
    return (
      <PaywallLock offerLockData={offerLockData}>
        <ChooseFrequencyLaterSteps
          offerLockData={offerLockData}
          viewer={viewer}
          componentType="paywall"
        />
      </PaywallLock>
    )
  }

  return (
    <ChooseFrequencyLaterContextProvider
      offerLockData={offerLockData}
      componentType="modal"
      isCompactModal={false}
    >
      <Upgrade2StepMessageLimitsModalContent offerLockData={offerLockData} viewer={viewer} />
    </ChooseFrequencyLaterContextProvider>
  )
}

function Upgrade2StepMessageLimitsModalContent({
  offerLockData,
  viewer,
}: Upgrade2StepMessageLimitsLockProps) {
  const { currentStep } = useChooseOfferFrequencyLaterContext()
  const { sendModalClosedEvent } = useChooseFrequencyLaterEvents()
  const isSecondStep = currentStep === ChooseFrequencyLaterModalSteps.SELECT_FREQUENCY

  const cards = offerLockData?.body?.cards || []
  const currentStepRef = useRef(currentStep)
  currentStepRef.current = currentStep

  useEffect(() => {
    return () => {
      if (cards[0]) sendModalClosedEvent(currentStepRef.current, cards[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const modalOfferLockData = isSecondStep
    ? {
        ...offerLockData,
        header: {
          ...offerLockData.header,
          title: offerLockData.header?.secondStepTitle || offerLockData.header?.title,
          subtitle: '',
        },
      }
    : offerLockData

  return (
    <ModalLock offerLockData={modalOfferLockData} size="md" headingSize="lg">
      <ChooseFrequencyLaterStepsContent offerLockData={offerLockData} viewer={viewer} />
    </ModalLock>
  )
}

Upgrade2StepMessageLimitsLock.fragments = {
  offerLockData: gql`
    fragment Upgrade2StepMessageLimitsLock_offerLockData on OfferLockData {
      header {
        title
        secondStepTitle
      }
      ...ModalLock_offerLockData
      ...PaywallLock_offerLockData
      ...ChooseFrequencyLaterSteps_offerLockData
    }
    ${ModalLock.fragments.offerLockData}
    ${PaywallLock.fragments.offerLockData}
    ${ChooseFrequencyLaterSteps.fragments.offerLockData}
  `,
  viewer: gql`
    fragment Upgrade2StepMessageLimitsLock_viewer on Profile {
      ...ChooseFrequencyLaterSteps_viewer
    }
    ${ChooseFrequencyLaterSteps.fragments.viewer}
  `,
}
